/**
 * The comment section under a video — §6/§8.
 *
 * Two of these sentences are the visible half of a decision the reader would otherwise never learn
 * about: the ordering they asked for was substituted, or the list is unreadable rather than empty.
 * Neither is an error the API reports, and both change what the reader is looking at — which is the
 * test a sentence has to pass to be here at all.
 */
export const comments = {
  heading: 'Comments',
  headingWithCount: (count: string): string => `${count} comments`,

  sort: {
    top: 'Top comments',
    new: 'Newest first',
  },

  replies: {
    hide: 'Hide replies',
    show: 'Show replies',
    /** The count is the *promised* one from the payload — see `pendingReplyCount` for why it can lie. */
    count: (count: string): string => count,
    showMore: 'Show more replies',
    showMoreCount: (count: string): string => `Show ${count} more`,
    noun: { one: 'reply', other: 'replies' },
  },

  notice: {
    /** Both orders were asked for; only one came back. */
    sortFallback: 'Top comments could not be loaded for this video, so these are the newest instead.',
    sortFallbackTruncated: (shown: number, total: string): string =>
      `Top comments could not be loaded for this video, so these are the ${shown} newest of ${total}.`,
    truncated: (shown: number, total: string): string => `Showing ${shown} of ${total}.`,
    /** What stands in for a count the response never carried. */
    unknownTotal: '?',
  },

  failure: {
    /** Said plainly, because nothing the reader does will change it. */
    upstreamBroken: 'The comments for this video could not be loaded from YouTube. Trying again will not help.',
    errorRetryable: 'Could not load the comments. The instance may be busy.',
    error: 'Could not load the comments.',
  },

  empty: {
    /** Measured: 6 of 6 live streams answer as though they have no comments — they have a chat. */
    live: 'Live streams carry a chat instead of comments, and Visious does not show it.',
    disabled: 'Comments are turned off for this video.',
    unreadable: 'The comments for this video could not be loaded. That does not mean there are none.',
    none: 'No comments yet.',
  },
} as const;
